import React, {useEffect, useState} from "react";
import {Form, Spinner, Stack} from "react-bootstrap";
import {EAIClient} from "Services/EAIClient";
import {useAppContext} from "@/contexts/app";
import {useToast} from "@/hooks/useToast";
import {Utils} from "@/common/utils";
import {useSchoolDomain} from "@/hooks/schools";
import {buildNewUiLoginUrl, resolveNewUiOrigin} from "@/common/auth/new-ui-url";

/**
 * Superadmin choice of which Enflux UI users land on at sign-in (EAI-5650/5651).
 * The flag has three states: `true` = new Enflux UI, `false` = this UI, and
 * `null` = no redirection at all — users stay on whichever UI they sign in to.
 * Mirrors platform-ui's NewUiSection (UI-277), which is the same control from
 * the other side.
 *
 * Read and write are both keyed by the chosen school (EAI-5689): the read is
 * `GET /schools/{id}/domain`, which resolves the school's subdomain and the flag
 * stored under it, and the write is the PATCH scoped to the same school. So a
 * superadmin who picked a school on another subdomain sees that school's
 * setting, not the one for the host they happen to be browsing. The PATCH
 * response still feeds the control directly for immediate feedback, and the
 * lookup is revalidated afterwards so the cache agrees with it.
 */

/** The flag's three states as radio values. */
type NewUiChoice = "new" | "current" | "none";

const toChoice = (useNewUi: boolean | null | undefined): NewUiChoice =>
    useNewUi === true ? "new" : useNewUi === false ? "current" : "none";

const fromChoice = (choice: NewUiChoice): boolean | null =>
    choice === "none" ? null : choice === "new";

const CHOICE_OPTIONS: {value: NewUiChoice, label: string}[] = [
    {value: "new", label: "New Enflux Interface"},
    {value: "current", label: "Current Enflux Interface"},
    {value: "none", label: "No redirection"},
];

const confirmCopy = (choice: NewUiChoice, subdomain: string) => ({
    new: {
        question: "Are you sure you want to send users to the New Enflux Interface?",
        detail: `Everyone signing in at ${subdomain} will land on the New Enflux Interface from now on.`,
        acceptText: "Use New Enflux Interface",
    },
    current: {
        question: "Are you sure you want to send users to the Current Enflux Interface?",
        detail: `Everyone signing in at ${subdomain} will land on the Current Enflux Interface from now on.`,
        acceptText: "Use Current Enflux Interface",
    },
    none: {
        question: "Are you sure you want to turn off sign-in redirection?",
        detail: `No one signing in at ${subdomain} will be redirected between the Enflux interfaces. Users stay on whichever interface they sign in to.`,
        acceptText: "Turn off redirection",
    },
}[choice]);

/**
 * Hops straight to the new UI after the school opted into it — the admin asked
 * everyone (themselves included) to be there, so leaving them here would just
 * defer the same hop to their next sign-in. Only when the flag we just flipped
 * governs the host being browsed: a superadmin updating a school on another
 * subdomain is managing it, not using it, and stays put. Returns whether the
 * browser is navigating away, so the caller can skip a toast nobody would see.
 */
const redirectedToNewUi = (updatedSubdomain: string): boolean => {
    if (window.location.hostname.split(".")[0] !== updatedSubdomain) {
        return false;
    }
    const origin = resolveNewUiOrigin();
    if (!origin) {
        return false;
    }
    // `assign`, not `replace`: the back button should return to Preferences.
    window.location.assign(buildNewUiLoginUrl(origin));
    return true;
};

const successMessage = (choice: NewUiChoice, subdomain: string) => ({
    new: `Users signing in at ${subdomain} will land on the New Enflux Interface.`,
    current: `Users signing in at ${subdomain} will land on the Current Enflux Interface.`,
    none: `Users signing in at ${subdomain} will stay on whichever Enflux interface they sign in to.`,
}[choice]);

export default function NewUiChoice() {
    const {dispatchError, showConfirmModal} = useAppContext();
    const {toast} = useToast();
    // The same localStorage key EAIClient scopes the PATCH to. Not the route
    // param (`:schoolId` is empty in several configs) and not
    // `Utils.getChosenSchool()`, which reports to Sentry on every render when unset.
    const schoolId = Utils.getEAIClientConfigs().school ?? undefined;
    const {data, error, isLoading, mutate} = useSchoolDomain(schoolId);
    const [isUpdating, setIsUpdating] = useState(false);
    const [useNewUi, setUseNewUi] = useState<boolean | null>(null);
    const [subdomain, setSubdomain] = useState("");

    useEffect(() => {
        if (!data) {
            return;
        }
        setUseNewUi(data.use_new_ui ?? null);
        setSubdomain(data.subdomain);
    }, [data]);

    const currentChoice = toChoice(useNewUi);

    const updateNewUi = async (nextChoice: NewUiChoice) => {
        setIsUpdating(true);
        try {
            const client = new EAIClient();
            const resp = await client.updateSchoolNewUi(fromChoice(nextChoice));
            const updated = resp.data.data;
            setUseNewUi(updated.use_new_ui ?? null);
            setSubdomain(updated.subdomain);
            // Keep the SWR cache in step with what was just written; the state above
            // already shows it, so no need to wait on the revalidation.
            void mutate();
            if (updated.use_new_ui === true && redirectedToNewUi(updated.subdomain)) {
                return;
            }
            toast({
                type: "success",
                title: "Interface updated",
                message: successMessage(toChoice(updated.use_new_ui ?? null), updated.subdomain)
            });
        } catch (e) {
            dispatchError(e);
        } finally {
            setIsUpdating(false);
        }
    };

    const handleChoiceClick = (nextChoice: NewUiChoice) => {
        if (nextChoice === currentChoice) {
            return;
        }
        const copy = confirmCopy(nextChoice, subdomain);
        showConfirmModal({
            element: (
                <div>
                    <div>{copy.question}</div>
                    <div>{copy.detail}</div>
                </div>),
            accept: () => updateNewUi(nextChoice),
            cancel: () => {},
            showActions: true,
            acceptText: copy.acceptText,
        });
    };

    return (
        <div>
            <h2 className="h5 mb-2 fw-bold">Enflux interface</h2>
            <p className="text-secondary mb-3">
                Choose which Enflux interface this school&apos;s users land on after signing in.
                The change applies to everyone under the subdomain{subdomain ? ` (${subdomain})` : ""} the
                next time they sign in.
            </p>
            {error
                ? <p className="text-danger mb-0">The current interface setting could not be loaded. Try again later.</p>
                : <Stack gap={2} className="ps-2">
                    <div className="fw-bold">
                        Sign-in interface {isUpdating && <Spinner size="sm" />}
                    </div>
                    <Form.Group>
                        {CHOICE_OPTIONS.map(({value, label}) => (
                            <Form.Check
                                key={value}
                                type="radio"
                                name="useNewUi"
                                id={`useNewUi-${value}`}
                                className="new-ui-choice"
                                // No pick while loading: a null flag legitimately means "No
                                // redirection", but an unloaded one must not look like it.
                                checked={!isLoading && currentChoice === value}
                                // Stays where the API says until the confirmation is accepted and
                                // the PATCH lands, so a cancelled pick never shows an unsaved state.
                                disabled={isLoading || isUpdating}
                                onChange={() => handleChoiceClick(value)}
                                label={<span className="ms-2">{label}</span>}
                            />
                        ))}
                    </Form.Group>
                </Stack>}
        </div>
    );
}
